import { Field, IFieldProps, TFieldTheme } from '../utility/field/Field'
import { IResultDropProps, ResultDrop, TResultValue } from '../result-drop/ResultDrop'
import React, {
  ComponentType,
  ForwardedRef,
  forwardRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { IIconProps } from '../../../types/IconProps.interface'
import { IWithDropdownProps, withDropdown } from '../../../lib/hoc/WithDropdown'
import { TriangleButton } from '../../buttons/triangle/TriangleButton'
import { IWithFieldSizeProps, withFieldSize } from '../../../lib/hoc/WithFieldSize'
import { IconCheck } from '../../icons/IconCheck'
import { IconCrossBold } from '../../icons/IconCrossBold'
import { IconButton } from '../../buttons/icon-button/IconButton'
import {
  DropdownPositioner,
  TDropdownPosition,
} from '../../../../lib/ui/dropdown-positioner/component/DropdownPositioner'
import { IOption } from '../search-input/BpSearchInput'
import { Chip } from './chip/Chip'
import { SearchField } from '../utility/search-field/SearchField'
import { IWithSearchProps, withSearch } from '../../../lib/hoc/WithSearch'
import ContentLoader from 'react-content-loader'
import { useTranslation } from 'react-i18next'
import { Preloader } from '../../cards/preloader/Preloader'
import { Tooltip } from 'antd'

export interface IBpSelect
  extends IWithDropdownProps,
    IWithFieldSizeProps,
    IWithSearchProps,
    IFieldProps,
    IResultDropProps {
  Icon?: ComponentType<IIconProps>
  placeholder?: string
  label?: string
  action?: JSX.Element
  disabled?: boolean
  error?: string
  success?: string
  options?: IOption[]
  title?: string
  showSearch?: boolean
  onInput?: () => void
  className?: string | undefined
  hideIcon?: boolean
  hasTranslate?: boolean
  bottomSection?: ReactNode
  enableChipLoad?: boolean
  /** Сторона дропдауна, см. DropdownPositioner */
  dropPosition?: TDropdownPosition
  /** Цвет выбранного значения в поле, перекрывает цвет по умолчанию (пагинация) */
  valueColor?: string
  /** Показывать выбранное чипами прямо в поле (настройки проекта, вкладка Locations) */
  showChips?: boolean
  /** Зовётся, когда дропдаун закрылся, а выбор за время его жизни поменялся */
  onDropClose?: () => void
}

const DEFAULT_ICON_COLOR = '#00326D'
/** Ширина счётчика «+N» вместе с отступом — держим её свободной, когда чипы не влезли. */
const CHIP_COUNTER_WIDTH = 49
/** gap между чипами, gap-[8px]. */
const CHIP_GAP = 8

const Select = forwardRef((props: IBpSelect, ref: ForwardedRef<HTMLSelectElement>) => {
  const {
    Icon = IconCheck,
    fieldSize = 'md',
    open,
    classSize,
    placeholder = '',
    label,
    action,
    options = [],
    title = '',
    onToggle,
    debouncedSearch,
    onInput,
    onClear,
    showSearch = true,
    className,
    theme: initialTheme = 'default',
    info,
    tooltip,
    loading = false,
    hideIcon = false,
    hasTranslate = false,
    error,
    success,
    required,
    bottomSection,
    enableChipLoad = true,
    dropPosition,
    valueColor,
    showChips = false,
    onDropClose,
    ...selectProps
  } = props
  const { t } = useTranslation()
  const { t: translateOption } = useTranslation('translation', { keyPrefix: 'fields.options' })
  const { disabled, value } = selectProps
  const [theme, setTheme] = useState<TFieldTheme>(initialTheme)
  const [selectedOptions, setSelectedOptions] = useState<IOption | IOption[]>()
  const [currentValue, setCurrentValue] = useState<TResultValue | undefined>(value)
  const [currentOpen, setCurrentOpen] = useState(open)
  const [message, setMessage] = useState<string>()
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [chipLoad, setChipLoad] = useState(true)
  const [visibleChips, setVisibleChips] = useState<number>(Number.MAX_SAFE_INTEGER)
  const [chipsBox, setChipsBox] = useState<HTMLDivElement | null>(null)
  const chipsBoxRef = useRef<HTMLDivElement | null>(null)
  const chipsRulerRef = useRef<HTMLDivElement>(null)
  const openedValue = useRef<string>()
  const pendingClear = useRef(false)

  useEffect(() => {
    if (theme === 'text') return

    if (disabled) {
      setCurrentOpen(false)
      setTheme('disabled')
    } else if (isDirty && error) {
      setTheme('error')
      setMessage(error)
    } else if (isDirty && success) {
      setTheme('success')
      setMessage(success)
    } else {
      setMessage('')
      currentOpen ? setTheme('focus') : setTheme('default')
    }
  }, [disabled, isDirty, error, success, currentOpen])

  useEffect(() => {
    if (!options.length && !value) {
      setCurrentValue(props.multiple ? [] : '')
    } else {
      setCurrentValue(value)
    }
  }, [value, options])

  useEffect(() => {
    if (open) {
      openedValue.current = JSON.stringify(currentValue)
      setIsDirty(true)
    } else if (openedValue.current !== undefined) {
      if (onDropClose && JSON.stringify(currentValue) !== openedValue.current) onDropClose()
      openedValue.current = undefined
    }

    setCurrentOpen(open)
  }, [open])

  useEffect(() => {
    if (!pendingClear.current) return

    const isEmpty = Array.isArray(selectedOptions)
      ? !selectedOptions.length
      : !selectedOptions?.value

    if (isEmpty) {
      pendingClear.current = false
      onDropClose && onDropClose()
    }
  }, [selectedOptions])

  useEffect(() => {
    options.length && enableChipLoad ? setChipLoad(true) : setChipLoad(false)
    const timer = setTimeout(() => setChipLoad(false), 250)

    return () => clearTimeout(timer)
  }, [options])

  /**
   * Сколько чипов влезает в поле.
   *
   * Ширины берём не у видимых чипов, а у невидимой линейки, где всегда лежат все чипы в потоке, —
   * поэтому замер не зависит от того, что мы спрятали в прошлый раз. Доступную ширину даёт
   * контейнер, чей размер задаёт только раскладка (flex-1, min-w-0, overflow-hidden).
   */
  const measureChips = () => {
    const box = chipsBoxRef.current
    const ruler = chipsRulerRef.current

    if (!box || !ruler) return

    const widths = Array.from(ruler.children, (chip) => chip.getBoundingClientRect().width)
    const width = box.getBoundingClientRect().width
    const rowWidth = (count: number) =>
      widths.slice(0, count).reduce((sum, chip, i) => sum + chip + (i ? CHIP_GAP : 0), 0)

    let fits = widths.length

    // не влезли все — резервируем место под «+N» и считаем, сколько помещается рядом с ним
    if (rowWidth(fits) > width) {
      fits = 0

      while (fits < widths.length && rowWidth(fits + 1) <= width - CHIP_COUNTER_WIDTH) fits += 1
    }

    setVisibleChips((prev) => (prev === fits ? prev : fits))
  }

  const chipsBoxCallback = useCallback((node: HTMLDivElement | null) => {
    chipsBoxRef.current = node
    setChipsBox(node)
  }, [])

  // выбор, тексты, перевод — всё, что меняет ширины, приходит с рендером
  useLayoutEffect(() => {
    if (showChips) measureChips()
  })

  // ширина поля меняется без рендера: сайдбар, ресайз окна, перестройка сетки
  useEffect(() => {
    if (!chipsBox || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => measureChips())

    observer.observe(chipsBox)

    return () => observer.disconnect()
  }, [chipsBox])

  const iconElement: JSX.Element = (
    <span className={`mr-[10px] w-[17px] ${theme === 'text' ? 'text-white' : 'text-focus-500'}`}>
      {<Icon color={DEFAULT_ICON_COLOR} />}
    </span>
  )
  const placeholderElement: JSX.Element = <span className='text-aqua-300'>{placeholder} </span>
  const nameElement: JSX.Element = (
    <span className='mr-[10px] flex items-center text-focus-500'>{title}</span>
  )

  const optionText: (text: string) => string = (text: string) =>
    hasTranslate && text.length ? translateOption(text) : text

  const valueElement: (text: string, color?: string) => JSX.Element = (
    text: string,
    color?: string,
  ) => (
    <span
      className={`flex-1 truncate ${
        title
          ? (color ?? 'text-default')
          : theme === 'text'
            ? `max-w-fit border-b-[1px] border-dashed border-b-[#FFFFFF66] ${color ?? 'text-white'}`
            : (color ?? 'text-focus-500')
      } `}
    >
      {text}
    </span>
  )

  const chipsElement: (values: IOption[]) => JSX.Element = (values: IOption[]) => (
    <div
      ref={chipsBoxCallback}
      // contain: inline-size — ширина контейнера не зависит от чипов внутри. Без этого min-content
      // видимого ряда всплывает вверх, и grid/flex-wrap раскладка страницы не даёт полю сжаться
      className='relative flex min-w-0 flex-1 items-center overflow-hidden [contain:inline-size]'
    >
      <div className='flex shrink-0 gap-[8px]'>
        {values.slice(0, visibleChips).map(({ value, text, allowDelete = true }: IOption) => (
          <Tooltip key={value} title={text}>
            <span className='chip max-w-[150px] shrink-0 p-[1px]'>
              <Chip allowDelete={allowDelete} onClick={() => removeChip(value)}>
                {text}
              </Chip>
            </span>
          </Tooltip>
        ))}
      </div>
      <ExtraCounter />
      <div
        ref={chipsRulerRef}
        aria-hidden
        className='pointer-events-none invisible absolute left-0 top-0 flex w-max gap-[8px]'
      >
        {values.map(({ value, text, allowDelete = true }: IOption) => (
          <span key={value} className='max-w-[150px] shrink-0 p-[1px]'>
            <Chip allowDelete={allowDelete}>{text}</Chip>
          </span>
        ))}
      </div>
    </div>
  )

  const ExtraCounter = () => {
    if (!Array.isArray(selectedOptions)) return null

    const hidden = selectedOptions.slice(visibleChips)

    return hidden.length ? (
      <Tooltip title={selectedOptions.map((option) => option.text).join(', ')}>
        <span
          className={
            'ml-[8px] flex h-[32px] w-[41px] shrink-0 items-center justify-center rounded-[4px] bg-focus-50 text-[14px] font-medium text-aqua-300'
          }
        >
          +{hidden.length}
        </span>
      </Tooltip>
    ) : null
  }

  const removeChip = (chipValue: string) => {
    if (!Array.isArray(selectedOptions)) return

    const filteredOptions = selectedOptions.filter((option: IOption) => option.value !== chipValue)

    setIsDirty(true)
    setSelectedOptions(filteredOptions)
    setCurrentValue(filteredOptions.map((o: IOption) => o.value))
  }

  const selectedCountElement: (values: IOption[]) => JSX.Element = (values: IOption[]) => (
    <Tooltip
      placement={'topLeft'}
      title={values.map(({ text }: IOption) => optionText(text)).join(', ')}
    >
      {valueElement(t('other.countSelected', { count: values.length }), 'text-default')}
    </Tooltip>
  )

  // у компактных полей единственное значение подсвечиваем так же, как счётчик
  const singleValueColor =
    valueColor ??
    (fieldSize === 'lg' || fieldSize === 'sm' || theme === 'text' ? undefined : 'text-default')

  const optionsElement = Array.isArray(selectedOptions)
    ? showChips
      ? chipsElement([...selectedOptions].sort((a) => (a.allowDelete === false ? -1 : 1)))
      : selectedOptions.length > 1
        ? selectedCountElement(selectedOptions)
        : selectedOptions.length === 1 &&
          valueElement(optionText(selectedOptions[0].text), singleValueColor)
    : selectedOptions && valueElement(optionText(selectedOptions.text), singleValueColor)

  const handleToggle = (e: MouseEvent) => {
    if (onToggle && !(e.target as Element).closest('.chip')) onToggle()
  }

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation()
    setIsDirty(true)
    pendingClear.current = true
    setCurrentValue(props.multiple ? [] : '')
  }

  // крестик только там, где значение действительно можно снять:
  // у поля с defaultValue сброс вернул бы дефолт, а не пустоту
  const canClear = (): boolean =>
    !required && !disabled && !props.defaultValue && !canShowPlaceholder()

  const canShowPlaceholder = (): boolean => {
    if (selectedOptions) {
      if (Array.isArray(selectedOptions)) {
        return selectedOptions.length <= 0 || !selectedOptions[0].value
      } else {
        return !selectedOptions.value
      }
    } else {
      return true
    }
  }

  if (loading && !currentOpen) {
    return theme === 'text' ? (
      <div
        className={`relative w-[20px] overflow-hidden ${fieldSize === 'sm' && 'h-[32px]'} ${fieldSize === 'md' && 'h-[44px]'} ${fieldSize === 'lg' && 'h-[56px]'} `}
      >
        <Preloader />
      </div>
    ) : (
      <ContentLoader
        backgroundColor='#f3f3f3'
        className={`w-full ${fieldSize === 'sm' && 'h-[32px]'} ${fieldSize === 'md' && 'h-[44px]'} ${fieldSize === 'lg' && 'h-[56px]'} ${label && 'mt-[26px]'}`}
        foregroundColor='#ecebeb'
        speed={2}
      >
        <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
      </ContentLoader>
    )
  }

  return (
    <div className={`relative ${disabled && 'pointer-events-none'} ${className}`}>
      {chipLoad && !disabled && theme !== 'text' && (
        <ContentLoader
          backgroundColor='#f3f3f3'
          className={`absolute z-10 w-full ${fieldSize === 'sm' && 'h-[32px]'} ${fieldSize === 'md' && 'h-[44px]'} ${fieldSize === 'lg' && 'h-[56px]'} ${label && 'mt-[26px]'} `}
          foregroundColor='#ecebeb'
          speed={2}
        >
          <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
        </ContentLoader>
      )}
      <Field
        action={action}
        fieldSize={fieldSize}
        info={info}
        label={label}
        message={message}
        required={required}
        theme={theme}
        tooltip={tooltip}
      >
        <div
          className={`flex min-w-0 select-none items-center ${!disabled && 'cursor-pointer'} ${fieldSize == 'lg' && 'pl-[14px]'} ${fieldSize == 'md' && 'pl-[12px]'} ${fieldSize == 'sm' && 'pl-[8px]'} ${theme == 'text' && 'pl-0'} ${classSize} `}
          onClick={handleToggle}
        >
          {(fieldSize == 'lg' || props.Icon) && !hideIcon && iconElement}
          <span
            className={`mr-[10px] flex min-w-0 flex-1 truncate ${fieldSize == 'lg' && 'text-[16px]'} ${fieldSize == 'md' && 'text-[15px]'} ${fieldSize == 'sm' && 'text-[14px]'} ${theme == 'text' && 'mr-0'} `}
          >
            {title && nameElement}
            {canShowPlaceholder() && <span className={'mr-[8px]'}>{placeholderElement}</span>}
            {optionsElement}
          </span>
          <span
            className={`flex items-center justify-center px-[16px] ${
              theme == 'text' && 'px-[8px]'
            }`}
          >
            {canClear() && (
              <IconButton className={'mr-[10px]'} onClick={handleClear}>
                <IconCrossBold color={'#6684A7'} />
              </IconButton>
            )}
            <TriangleButton
              color={theme === 'text' ? '#FFF' : '#A059FF'}
              position={currentOpen ? 'top' : 'down'}
            ></TriangleButton>
          </span>
        </div>
        <DropdownPositioner open={currentOpen} position={dropPosition}>
          <div
            className={`flex flex-col overflow-hidden rounded-[6px] shadow-outline shadow-default ${theme == 'text' && 'min-w-[200px]'}`}
          >
            {showSearch && (
              <div className='border-b border-default/[0.07] bg-white'>
                <SearchField
                  disabled={disabled}
                  fieldSize={fieldSize}
                  onClear={onClear}
                  onInput={onInput}
                />
              </div>
            )}
            <ResultDrop
              {...selectProps}
              ref={ref}
              noShadow
              hasTranslate={hasTranslate}
              loading={loading}
              options={options}
              pinTop={!!currentOpen}
              required={required}
              search={debouncedSearch}
              value={currentValue}
              onSelected={(value: IOption | IOption[] | undefined) => {
                setSelectedOptions(value)

                if (!props.multiple && props.open) {
                  onToggle && onToggle()
                }
              }}
            ></ResultDrop>
            {bottomSection && (
              <div className={'border-t border-default/[0.07] bg-white'}>{bottomSection}</div>
            )}
          </div>
        </DropdownPositioner>
      </Field>
    </div>
  )
})
export const BpSelect = withDropdown(withFieldSize(withSearch(Select)))
